import { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";

import cx from "@hsl/core/utils/cx";

export interface Props {
    open: boolean;
    onClose: () => void;
    title: string;
    initialFocus?: React.MutableRefObject<HTMLElement | null>;
    showCloseButton?: boolean;
    panelClassName?: string;
    children: React.ReactNode;
}

const Modal = ({
    open,
    onClose,
    children,
    title,
    initialFocus,
    showCloseButton = true,
    panelClassName,
}: Props) => {
    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog
                as="div"
                className="relative z-[60]"
                onClose={onClose}
                initialFocus={initialFocus}
            >
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
                </Transition.Child>
                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel
                                className={cx(
                                    "divide-platinum relative flex w-[500px] max-w-full transform flex-col items-stretch divide-y overflow-hidden rounded-md bg-white",
                                    panelClassName,
                                )}
                            >
                                <div className="flex items-center justify-between p-8">
                                    <Dialog.Title
                                        as="h3"
                                        className="text-charcoal text-xl"
                                    >
                                        {title}
                                    </Dialog.Title>
                                    {showCloseButton && (
                                        <button
                                            role="button"
                                            className="fill-charcoal hover:fill-warm-gray rounded-full p-2 transition-colors"
                                        >
                                            <svg
                                                id="cross"
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="14.728"
                                                height="14.728"
                                                viewBox="0 0 14.728 14.728"
                                                onClick={onClose}
                                            >
                                                <path
                                                    d="M17.414,16l5.657,5.657a1,1,0,1,1-1.414,1.414L16,17.414l-5.657,5.657a1,1,0,1,1-1.414-1.414L14.586,16,8.929,10.343a1,1,0,1,1,1.414-1.414L16,14.586l5.657-5.657a1,1,0,1,1,1.414,1.414Z"
                                                    transform="translate(-8.636 -8.636)"
                                                />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                                {children}
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
};

export default Modal;
